import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useMemory, useAddMemory, useDeleteMemory } from "../../hooks/useMemory";
import { errorMessage } from "../../hooks/useServers";

const KIND_LABEL = { advertencia: "⚠️ Advertencias", decision: "Decisiones", hecho: "Hechos", preferencia: "Preferencias" };
const KIND_ORDER = ["advertencia", "decision", "hecho", "preferencia"];

// «Memoria»: lo que el Orquestador recuerda de este proyecto en cada conversación. Se puede agregar y borrar.
export function MemoryDialog({ open, onOpenChange, project }) {
  const projectId = project?.id;
  const { data, isLoading } = useMemory(projectId, open);
  const add = useAddMemory(projectId);
  const remove = useDeleteMemory(projectId);
  const [kind, setKind] = useState("hecho");
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const notes = data?.notes || [];
  const maxChars = data?.max_chars || 300;

  const handleAdd = async () => {
    setError("");
    try {
      await add.mutateAsync({ kind, text });
      setText("");
    } catch (err) {
      setError(errorMessage(err, "No se pudo guardar la nota."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="memory-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Memoria de {project?.name}</DialogTitle>
          <DialogDescription>
            Lo que el Orquestador aprendió de este proyecto y recuerda en cada conversación. Nunca guarda contraseñas. También queda reflejado en Drive
            (Memoria_del_Agente.md).
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
        {!isLoading && notes.length === 0 && (
          <p data-testid="memory-empty" className="text-sm text-muted-foreground">
            Todavía no hay notas. El Orquestador anotará aquí lo duradero que vaya aprendiendo, y tú también puedes agregar las tuyas.
          </p>
        )}

        <div className="space-y-3">
          {KIND_ORDER.map((k) => {
            const group = notes.filter((n) => n.kind === k);
            if (group.length === 0) return null;
            return (
              <div key={k} data-testid={`memory-group-${k}`} className="space-y-1">
                <p className="text-xs font-medium">{KIND_LABEL[k]}</p>
                {group.map((n) => (
                  <div key={n.id} data-testid={`memory-note-${n.id}`} className="flex items-start gap-2 rounded-lg border border-border px-2.5 py-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="break-words">{n.text}</p>
                      <p className="text-xs text-muted-foreground">{(n.created_at_mx || "").slice(0, 10)} · {n.source === "usuario" ? "tú" : "el Orquestador"}</p>
                    </div>
                    <button
                      type="button"
                      data-testid={`memory-delete-${n.id}`}
                      title="Olvidar esta nota"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(n.id)}
                      className="text-muted-foreground hover:text-red-500 shrink-0 mt-0.5"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-xs font-medium">Agregar una nota</p>
          <div className="flex gap-2">
            <select
              data-testid="memory-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded-md border border-border bg-transparent px-2 py-1.5 text-sm"
            >
              {KIND_ORDER.map((k) => (
                <option key={k} value={k}>{KIND_LABEL[k].replace("⚠️ ", "")}</option>
              ))}
            </select>
            <Textarea
              data-testid="memory-text"
              rows={2}
              value={text}
              maxLength={maxChars}
              onChange={(e) => setText(e.target.value)}
              placeholder="Una frase clara, p. ej. «El CHR solo se alcanza desde WEB»"
              className="text-sm"
            />
          </div>
          {error && <p data-testid="memory-error" className="text-xs text-red-500">{error}</p>}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{data ? `${data.count} de ${data.limit} notas` : ""}</span>
            <Button data-testid="memory-add" size="sm" onClick={handleAdd} disabled={!text.trim() || add.isPending}>
              {add.isPending ? "Guardando…" : "Guardar nota"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
